import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiHome, FiDollarSign, FiMessageSquare, FiUser, FiBookmark, FiSettings, FiLogOut, FiMoon, FiSun, FiBarChart2, FiPieChart, FiTarget, FiShield, FiArrowRight, FiArrowDown } from '../../utils/icons';
import * as FiIcons from 'react-icons/fi';
import mascot from '../../assets/placeholder.png';
import { useLanguage } from '../../utils/languageContext';
import { useTheme } from '../../utils/ThemeContext';
import { withAlpha } from '../../utils/colorUtils';

// Create icon components for missing icons
const FiChevronLeft = ({ size, ...props }: { size?: number, [key: string]: any }) => (
  <FiIcons.FiChevronLeft size={size} {...props} />
);

const FiChevronRight = ({ size, ...props }: { size?: number, [key: string]: any }) => (
  <FiIcons.FiChevronRight size={size} {...props} />
);

interface NavbarProps {
  mobile?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
}

interface NavSectionProps {
  title: string;
  items: any[];
  isExpanded: boolean;
  navExpanded: boolean;
  toggleExpand: () => void;
  activeItemPath: string;
}

const Navbar: React.FC<NavbarProps> = ({ mobile = false, expanded = true, onToggleExpand }) => {
  const location = useLocation();
  const { translations } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const isDarkMode = theme === 'dark';
  
  // Track expanded sections for collapsible nav
  const [expandedSections, setExpandedSections] = useState({
    general: true,
    ai: true,
    personal: true,
    settings: true
  });
  
  // Toggle section expansion
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Mock user data - in a real app this would come from authentication
  const user = {
    name: 'Bill',
    isLoggedIn: true
  };
  
  const generalNavItems = [
    { path: '/', label: translations.home, icon: <FiHome size={20} /> },
    { path: '/dashboard', label: 'Dashboard', icon: <FiBarChart2 size={20} /> },
    { path: '/finances', label: translations.myFinances, icon: <FiDollarSign size={20} /> },
    { path: '/chat', label: translations.chatWithAdvisor, icon: <FiMessageSquare size={20} /> },
  ];
  
  const aiFeatureNavItems = [
    { path: '/financial-intelligence', label: translations.aiIntelligence, icon: <FiBarChart2 size={20} /> },
    { path: '/consumption-insights', label: translations.spendingInsights, icon: <FiPieChart size={20} /> },
    { path: '/risk-assessment', label: translations.riskProfile, icon: <FiTarget size={20} /> },
    { path: '/fraud-detection', label: translations.fraudProtection, icon: <FiShield size={20} /> },
  ];
  
  const personalNavItems = [
    { path: '/profile', label: translations.personalInformation, icon: <FiUser size={20} /> },
    { path: '/saved', label: translations.savedArticles, icon: <FiBookmark size={20} /> },
    { path: '/account-security', label: translations.accountSecurity, icon: <FiShield size={20} /> },
  ];
  
  const settingsNavItems = [
    { path: '/settings', label: translations.settings, icon: <FiSettings size={20} /> },
    { path: '/logout', label: translations.logout, icon: <FiLogOut size={20} /> },
  ];
  
  const NavSection = ({ title, items, isExpanded, navExpanded, toggleExpand, activeItemPath }: NavSectionProps) => (
    <div className="mb-4">
      {/* Only show section titles when navbar is expanded */}
      {navExpanded && (
        <button 
          onClick={toggleExpand}
          className="w-full flex items-center justify-between text-sm font-medium text-text text-opacity-70 mb-1 px-4 py-2 hover:bg-card-hover rounded-lg transition-colors"
        >
          <span>{title}</span>
          {isExpanded ? <FiArrowDown size={16} /> : <FiArrowRight size={16} />}
        </button>
      )}
      
      {/* Always show items in collapsed navbar, only show items when section is expanded in expanded navbar */}
      {(!navExpanded || isExpanded) && (
        <div className="space-y-1 ml-2">
          {items.map((item) => (
            <Link 
              key={item.path} 
              to={item.path}
              className={`group flex items-center px-4 py-3 rounded-lg transition-all duration-300 ${
                location.pathname === item.path
                  ? 'bg-gradient-to-r from-primary via-secondary to-accent text-white animate-pulse-subtle' // Futuristic AI gradient with subtle animation
                  : 'text-text hover:bg-card-hover hover:text-primary'
              }`}
            >
              <div className={`${
                location.pathname === item.path 
                  ? 'text-white' // Icon is white for selected item
                  : 'text-text group-hover:text-primary'
                } mr-3 transition-colors duration-300`}>
                {item.icon}
              </div>
              {(navExpanded || mobile) && (
                <span className="transition-transform duration-300">{item.label}</span>
              )}
              {!navExpanded && !mobile && location.pathname === item.path && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-card rounded-md text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {item.label}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
  
  // For mobile view, we don't need to wrap in the fixed container
  // it's already wrapped in the mobile sidebar
  if (mobile) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-grow">
          <div className="flex items-center mb-8 px-4">
            <Link to="/" className="flex items-center">
              <div className="w-10 h-10 mr-3 rounded-lg overflow-hidden">
                <img src={mascot} alt="Matrix Finance Mascot" className="w-full h-full object-cover" />
              </div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text">MATRIX FINANCE</h1>
            </Link>
          </div>
          
          {user.isLoggedIn && (
            <div className="mb-8 px-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary to-accent flex items-center justify-center text-white font-bold">
                  {user.name[0]}
                </div>
                <div>
                  <p className="text-sm text-text text-opacity-70">{translations.welcome},</p>
                  <p className="font-medium">{user.name}</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-4 overflow-y-auto max-h-[calc(100vh-240px)] hide-scrollbar">
            <NavSection 
              title={translations.general} 
              items={generalNavItems} 
              isExpanded={expandedSections.general}
              navExpanded={true}
              toggleExpand={() => toggleSection('general')}
              activeItemPath={location.pathname}
            />
            
            <NavSection 
              title={translations.aiFeatures} 
              items={aiFeatureNavItems} 
              isExpanded={expandedSections.ai}
              navExpanded={true}
              toggleExpand={() => toggleSection('ai')}
              activeItemPath={location.pathname}
            />
            
            <NavSection 
              title={translations.personal} 
              items={personalNavItems} 
              isExpanded={expandedSections.personal}
              navExpanded={true}
              toggleExpand={() => toggleSection('personal')}
              activeItemPath={location.pathname}
            />
            
            <NavSection 
              title={translations.settings} 
              items={settingsNavItems} 
              isExpanded={expandedSections.settings}
              navExpanded={true}
              toggleExpand={() => toggleSection('settings')}
              activeItemPath={location.pathname}
            />
          </div>
        </div>
        
        <div className="px-4 pt-4 mb-4 mt-auto border-t border-border">
          <button 
            onClick={toggleTheme}
            className="neo-button w-full flex items-center justify-between bg-card hover:bg-card-hover p-3 rounded-lg transition-all hover:shadow-glow"
          >
            <span className="flex items-center">
              <span className="mr-3 text-primary">
                {isDarkMode ? <FiSun size={20} /> : <FiMoon size={20} />}
              </span>
              <span>{translations.darkMode}</span>
            </span>
            <div className={`w-10 h-5 rounded-full p-1 transition-colors ${isDarkMode ? 'bg-primary' : 'bg-gray-600'}`}>
              <div className={`h-3 w-3 rounded-full bg-white transform transition-transform ${isDarkMode ? 'translate-x-5' : 'translate-x-0'}`} />
            </div>
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-screen fixed left-0 top-0 flex z-10">
      {/* Collapsed Toggle Button */}
      {!expanded && (
        <button 
          onClick={onToggleExpand}
          className="absolute top-4 right-0 translate-x-1/2 z-20 p-1 bg-card border border-border rounded-full shadow-lg hover:shadow-glow transition-all"
        >
          <FiChevronRight size={18} className="text-primary" />
        </button>
      )}
      
      <nav className={`neo-card h-full flex flex-col transition-all duration-500 ease-in-out ${
        expanded 
          ? 'w-64 border-r border-border' 
          : 'w-20 border-r border-border'
      }`}>
        <div className="flex items-center px-4 py-4 mb-6">
          <Link to="/" className="flex items-center">
            <div className="w-10 h-10 rounded-lg overflow-hidden">
              <img src={mascot} alt="Matrix Finance Mascot" className="w-full h-full object-cover" />
            </div>
            {expanded && (
              <h1 className="ml-3 text-xl font-bold bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text whitespace-nowrap">MATRIX FINANCE</h1>
            )}
          </Link>
          
          {expanded && (
            <button 
              onClick={onToggleExpand}
              className="ml-auto p-1 text-text text-opacity-70 hover:text-primary transition-colors"
            >
              <FiChevronLeft size={20} />
            </button>
          )}
        </div>
        
        {user.isLoggedIn && (
          <div className="mb-6 px-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary to-accent flex items-center justify-center text-white font-bold">
                {user.name[0]}
              </div>
              {expanded && (
                <div>
                  <p className="text-sm text-text text-opacity-70">{translations.welcome},</p>
                  <p className="font-medium">{user.name}</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="px-2 overflow-y-auto hide-scrollbar flex-grow max-h-[calc(100vh-230px)]">
          <div className="space-y-4">
            <NavSection 
              title={translations.general} 
              items={generalNavItems} 
              isExpanded={expandedSections.general}
              navExpanded={expanded}
              toggleExpand={() => toggleSection('general')}
              activeItemPath={location.pathname}
            />
            
            <NavSection 
              title={translations.aiFeatures} 
              items={aiFeatureNavItems} 
              isExpanded={expandedSections.ai}
              navExpanded={expanded}
              toggleExpand={() => toggleSection('ai')}
              activeItemPath={location.pathname}
            />
            
            <NavSection 
              title={translations.personal} 
              items={personalNavItems} 
              isExpanded={expandedSections.personal}
              navExpanded={expanded}
              toggleExpand={() => toggleSection('personal')}
              activeItemPath={location.pathname}
            />
            
            <NavSection 
              title={translations.settings} 
              items={settingsNavItems} 
              isExpanded={expandedSections.settings}
              navExpanded={expanded}
              toggleExpand={() => toggleSection('settings')}
              activeItemPath={location.pathname}
            />
          </div>
        </div>
        
        <div className="mb-6 pt-4 px-4 border-t border-border border-opacity-30">
          <button 
            onClick={toggleTheme}
            className={`flex items-center justify-center rounded-lg p-3 transition-all hover:shadow-glow ${
              expanded ? 'w-full' : 'w-14 h-14 mx-auto'
            } bg-card hover:bg-card-hover`}
          >
            <span className="text-primary">
              {isDarkMode ? <FiSun size={24} /> : <FiMoon size={24} />}
            </span>
            {expanded && <span className="ml-3">{translations.darkMode}</span>}
            {expanded && (
              <div className={`ml-auto w-10 h-5 rounded-full p-1 transition-colors ${isDarkMode ? 'bg-primary' : 'bg-gray-600'}`}>
                <div className={`h-3 w-3 rounded-full bg-white transform transition-transform ${isDarkMode ? 'translate-x-5' : 'translate-x-0'}`} />
              </div>
            )}
          </button>
        </div>
      </nav>
    </div>
  );
};

export default Navbar; 